import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, onAuthStateChanged, signOut, User } from '@angular/fire/auth';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { NbAuthService } from '@nebular/auth';
import { NbToastrService } from '@nebular/theme';
import { take } from 'rxjs/operators';
import Swal from 'sweetalert2';

interface SessionSecuritySettings {
  sessionTimeoutEnabled: boolean;
  sessionTimeoutMinutes: number;
  sessionWarningMinutes: number;
}

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly defaultSettings: SessionSecuritySettings = {
    sessionTimeoutEnabled: false,
    sessionTimeoutMinutes: 30,
    sessionWarningMinutes: 2,
  };

private readonly activityEvents = [
  'click',
  'keydown',
  'mousedown',
  'scroll',
  'touchstart',
];

private activityHandler = (event: Event) =>
  this.markActivity(event);

private sessionWarningOpen = false;
  private idleInterval: any;
  private settings = this.defaultSettings;
  private activeUser: User | null = null;
  private listening = false;
  private loggingOut = false;
  private warningShown = false;
  private lastActivityAt = Date.now();

  constructor(
    private auth: Auth,
    private nbAuthService: NbAuthService,
    private firestore: Firestore,
    private router: Router,
    private toastr: NbToastrService,
    private zone: NgZone
  ) {}

  init(): void {
    onAuthStateChanged(this.auth, async user => {
      this.activeUser = user;
      this.loggingOut = false;

      // Do not let the session-timeout service read Firestore on authentication or registration pages.
      if (!user || this.isAuthRoute()) {
        this.stop();
        return;
      }

      const canRun = await this.canRunForUser(user.uid);
      if (!canRun) {
        this.stop();
        return;
      }

      await this.loadCompanySettings();
      this.applySettings();
    });
  }

  async refreshSettings(): Promise<void> {
    if (!this.activeUser || this.isAuthRoute()) return;

    const canRun = await this.canRunForUser(this.activeUser.uid);
    if (!canRun) {
      this.stop();
      return;
    }

    await this.loadCompanySettings();
    this.applySettings();
  }

  private async canRunForUser(uid: string): Promise<boolean> {
    try {
      const userSnap = await getDoc(doc(this.firestore, 'users', uid));

      // Do not start the service when the users collection or document does not yet exist.
      if (!userSnap.exists()) {
        return false;
      }

      const userData: any = userSnap.data();
      return (userData.status || '').toLowerCase() === 'approved';
    } catch (err: any) {
      const message = String(err?.message || '').toLowerCase();

      // Silently disable session timeout when Firestore is missing or offline.
      if (
        err?.code === 'not-found' ||
        err?.code === 'unavailable' ||
        message.includes('database (default) does not exist') ||
        message.includes('client is offline')
      ) {
        console.warn('Session timeout disabled until Firestore/users is available:', err);
      } else {
        console.error('Failed to check user session eligibility:', err);
      }

      return false;
    }
  }

  private async loadCompanySettings(): Promise<void> {
    this.settings = { ...this.defaultSettings };

    try {
      const policySnap = await getDoc(doc(this.firestore, 'systemPolicies', 'sessionSecurity'));
      const security: any = policySnap.exists() ? policySnap.data() : {};

      this.settings = {
        sessionTimeoutEnabled: !!security.enabled,
        sessionTimeoutMinutes: this.toBoundedNumber(security.sessionTimeoutMinutes, 30, 1, 1440),
        sessionWarningMinutes: this.toBoundedNumber(security.sessionWarningMinutes, 2, 0, 60),
      };
    } catch (err) {
      console.warn('Session timeout settings unavailable. Using defaults:', err);
      this.settings = { ...this.defaultSettings };
    }
  }

  private applySettings(): void {
    this.stop();

    if (!this.settings.sessionTimeoutEnabled || this.isAuthRoute()) {
      return;
    }

    this.startListening();
    this.startIdleWatcher();
  }

private startListening(): void {
  if (this.listening) {
    return;
  }

  this.zone.runOutsideAngular(() => {
    this.activityEvents.forEach(eventName => {
      window.addEventListener(
        eventName,
        this.activityHandler,
        { passive: true }
      );
    });
  });

  this.listening = true;
}

private markActivity(event?: Event): void {
  if (
    !this.activeUser ||
    !this.settings.sessionTimeoutEnabled ||
    this.loggingOut
  ) {
    return;
  }

  const target =
    event?.target instanceof Element
      ? event.target
      : null;

  /*
   * Do not handle clicks or mousedown events inside SweetAlert here.
   * Otherwise, before the Swal confirm/cancel handlers execute,
   * the session service will close the popup.
   */
  if (target?.closest('.swal2-container')) {
    return;
  }

  this.lastActivityAt = Date.now();
  this.warningShown = false;

  /*
   * Close only the warning popup created by session timeout.
   * Never close other SweetAlerts in the application.
   */
  if (
    this.sessionWarningOpen &&
    Swal.isVisible()
  ) {
    this.sessionWarningOpen = false;
    Swal.close();
  }
}

  private startIdleWatcher(): void {
    if (!this.activeUser || !this.settings.sessionTimeoutEnabled || this.loggingOut) return;

    this.clearTimers();
    this.lastActivityAt = Date.now();
    this.warningShown = false;

    this.zone.runOutsideAngular(() => {
      this.idleInterval = setInterval(() => this.zone.run(() => this.checkIdleTime()), 1000);
    });
  }

  private checkIdleTime(): void {
    if (!this.activeUser || !this.settings.sessionTimeoutEnabled || this.loggingOut) return;

    const timeoutMs = this.settings.sessionTimeoutMinutes * 60 * 1000;
    const warningMs = this.getEffectiveWarningMs(timeoutMs);
    const idleMs = Date.now() - this.lastActivityAt;

    if (warningMs > 0 && idleMs >= timeoutMs - warningMs && !this.warningShown) {
      this.showExpiryWarning(timeoutMs - idleMs);
    }

    if (idleMs >= timeoutMs) {
      this.expireSession();
    }
  }

  private getEffectiveWarningMs(timeoutMs: number): number {
    const requestedWarningMs = this.settings.sessionWarningMinutes * 60 * 1000;
    if (requestedWarningMs <= 0) return 0;
    if (requestedWarningMs < timeoutMs) return requestedWarningMs;
    return Math.max(1000, Math.floor(timeoutMs / 2));
  }

private showExpiryWarning(
  remainingMs: number
): void {
  if (
    this.warningShown ||
    this.sessionWarningOpen ||
    this.loggingOut
  ) {
    return;
  }

  this.warningShown = true;
  this.sessionWarningOpen = true;

  const remainingMinutes = Math.max(
    1,
    Math.ceil(remainingMs / 60000)
  );

  Swal.fire({
    title: 'Session expiring',
    text:
      `You will be logged out in about ` +
      `${remainingMinutes} minute(s) due to inactivity.`,
    icon: 'warning',
    confirmButtonText: 'Stay logged in',

    showCancelButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    heightAuto: false,

    customClass: {
      container: 'session-timeout-swal',
    },
  })
    .then(result => {
      this.sessionWarningOpen = false;

      if (
        result.isConfirmed &&
        !this.loggingOut
      ) {
        /*
         * This is being called without an event,
         * so activity will reset correctly.
         */
        this.markActivity();
      }
    })
    .catch(error => {
      this.sessionWarningOpen = false;

      console.error(
        'Session warning dialog failed:',
        error
      );
    });
}

  private async expireSession(): Promise<void> {
    if (this.loggingOut) return;

    this.loggingOut = true;
    this.stop();

    try {
      localStorage.removeItem('userData');
      localStorage.removeItem('auth_app_token');
      this.nbAuthService.logout('email').pipe(take(1)).subscribe(
        () => this.finishExpiredLogout(),
        async err => {
          console.error('Failed to clear Nebular session:', err);
          await signOut(this.auth).catch(firebaseErr => {
            console.error('Failed to sign out Firebase session:', firebaseErr);
          });
          this.finishExpiredLogout();
        }
      );
    } catch (err) {
      console.error('Failed to sign out expired session:', err);
      this.finishExpiredLogout();
    }
  }

  private finishExpiredLogout(): void {
    this.toastr.danger('Your session expired because of inactivity.', 'Logged out');
    this.router.navigate(['/auth/login'], { queryParams: { expired: 'session' } });
  }

private stop(): void {
  this.clearTimers();

  this.warningShown = false;

  if (
    this.sessionWarningOpen &&
    Swal.isVisible()
  ) {
    this.sessionWarningOpen = false;
    Swal.close();
  }

  if (!this.listening) {
    return;
  }

  this.activityEvents.forEach(eventName => {
    window.removeEventListener(
      eventName,
      this.activityHandler
    );
  });

  this.listening = false;
}

  private clearTimers(): void {
    if (this.idleInterval) clearInterval(this.idleInterval);
    this.idleInterval = null;
  }

  private getStoredUserData(): any {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch {
      return {};
    }
  }

  private toBoundedNumber(value: any, fallback: number, min: number, max: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  }

  private isAuthRoute(): boolean {
    return this.router.url.startsWith('/auth');
  }
}
